import clsx from 'clsx';
import type { HTMLAttributes } from 'react';
import { tlwTwMerge } from '@trg/utils';
import { useMergeState } from '../../hooks';

export type SliderDotsProps = Omit<
  HTMLAttributes<HTMLUListElement>,
  'defaultChecked' | 'defaultValue' | 'children' | 'onChange'
> & {
  defaultCurrent?: number;
  current?: number;
  totalCount?: number;
  theme?: 'light' | 'dark';
  size?: 'small' | 'medium' | 'large';
  activeClassName?: string;
  onChange?: (current: number) => void;
};

export function SliderDots(props: SliderDotsProps) {
  const {
    defaultCurrent,
    current,
    totalCount = 4,
    theme = 'light',
    size = 'medium',
    activeClassName,
    className,
    onChange,
    ...restProps
  } = props;

  const isLight = theme === 'light';
  const isDark = theme === 'dark';

  const [value, setValue] = useMergeState(0, {
    defaultValue: defaultCurrent,
    value: current,
  });

  const handleItemClick = (index: number) => {
    if (!('current' in props)) {
      setValue(index);
    }
    onChange?.(index);
  };

  return (
    <ul
      className={tlwTwMerge('tlw-py-2 tlw-flex tlw-gap-2', className)}
      {...restProps}
    >
      {Array.from({ length: totalCount }).map((_, index) => (
        <li
          key={index}
          className={clsx(
            'tlw-rounded-full tlw-duration-300 tlw-cursor-pointer',
            {
              'tlw-bg-gray-500': isLight,
              'tlw-bg-gray-400': isDark,
              'tlw-size-1': size === 'small',
              'tlw-size-2': size === 'medium',
              'tlw-size-3': size === 'large',
            },
            value === index && [
              { 'tlw-bg-pr': isLight, 'tlw-bg-white': isDark },
              activeClassName,
            ]
          )}
          onClick={() => handleItemClick(index)}
        />
      ))}
    </ul>
  );
}
